import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaMousePointer, FaKeyboard, FaListUl, FaUpload, FaPaperPlane, FaUndo, FaMagic, FaSave, FaPlay, FaCheck, FaGlobe, FaEye } from 'react-icons/fa';
import './RecipeStudio.css';

// Recipe studio (backend plan C5): record a portal flow once from the
// production IP (the mail service drives the browser; the operator steers it
// element by element), review the draft as a form, run it in test mode, and
// publish it for the account. Secrets typed here stay in the browser session.

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
const BASE = '/api/logistics/carrier-recipes';
const VARIABLE_CHOICES = ['tracking_number', 'motif', 'message', 'comments', 'package_content', 'package_value', 'recipient_phone', 'order_number', 'customer_email', 'username', 'password'];

const RecipeStudio = ({ carrierName, initialRecipe = null, onClose, onSaved }) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState(initialRecipe ? 'review' : 'record');
    const [operation, setOperation] = useState(initialRecipe?.operation || 'create');
    const [startUrl, setStartUrl] = useState('');
    const [session, setSession] = useState(null);
    const [frame, setFrame] = useState(null);
    const [frameKey, setFrameKey] = useState(0);
    const [selected, setSelected] = useState(null);
    const [value, setValue] = useState('');
    const [variable, setVariable] = useState('');
    const [secret, setSecret] = useState(false);
    const [waitText, setWaitText] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [draft, setDraft] = useState(initialRecipe);
    const [testResult, setTestResult] = useState(null);
    const [saved, setSaved] = useState(initialRecipe?.db_id ? initialRecipe : null);
    const [filter, setFilter] = useState('');
    const sessionRef = useRef(null);

    const fail = (e, fallback) => {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : fallback);
    };

    useEffect(() => () => {
        if (sessionRef.current) axios.delete(`${BASE}/studio/sessions/${sessionRef.current}`, authHeaders()).catch(() => {});
    }, []);

    const openSession = async () => {
        setBusy(true); setError(null);
        try {
            const response = await axios.post(`${BASE}/studio/sessions`, { carrier: carrierName, operation, start_url: startUrl || null }, authHeaders());
            setSession(response.data.session);
            sessionRef.current = response.data.session.id;
            setFrame(response.data.frame);
            setFrameKey((k) => k + 1);
        } catch (e) { fail(e, t('recipeStudio.openError', 'Impossible d’ouvrir la session d’enregistrement.')); }
        finally { setBusy(false); }
    };

    const act = async (action, extra = {}) => {
        if (!session) return;
        setBusy(true); setError(null);
        try {
            const body = { action, k: selected?.k ?? null, value: value || null, variable: variable || null, secret, wait_text: waitText || null, ...extra };
            const response = await axios.post(`${BASE}/studio/sessions/${session.id}/act`, body, authHeaders());
            setFrame(response.data.frame);
            setFrameKey((k) => k + 1);
            setSelected(null); setValue(''); setVariable(''); setSecret(false); setWaitText('');
        } catch (e) { fail(e, t('recipeStudio.actError', 'L’action a échoué.')); }
        finally { setBusy(false); }
    };

    const undo = async () => {
        if (!session) return;
        setBusy(true);
        try {
            const response = await axios.post(`${BASE}/studio/sessions/${session.id}/undo`, {}, authHeaders());
            setFrame(response.data.frame); setFrameKey((k) => k + 1);
        } catch (e) { fail(e, t('recipeStudio.actError', 'L’action a échoué.')); }
        finally { setBusy(false); }
    };

    const makeDraft = async (review) => {
        if (!session) return;
        setBusy(true); setError(null);
        try {
            const response = await axios.post(`${BASE}/studio/sessions/${session.id}/draft?review=${review ? 'true' : 'false'}`, {}, authHeaders());
            setDraft(response.data.draft);
            setMode('review');
        } catch (e) { fail(e, t('recipeStudio.draftError', 'Impossible de produire le brouillon.')); }
        finally { setBusy(false); }
    };

    const updateStep = (index, patch) => setDraft((d) => ({ ...d, steps: d.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)) }));
    const removeStep = (index) => setDraft((d) => ({ ...d, steps: d.steps.filter((_, i) => i !== index) }));
    const moveStep = (index, dir) => setDraft((d) => {
        const steps = [...d.steps]; const j = index + dir;
        if (j < 0 || j >= steps.length) return d;
        [steps[index], steps[j]] = [steps[j], steps[index]];
        return { ...d, steps };
    });

    const save = async () => {
        setBusy(true); setError(null);
        try {
            const response = await axios.post(`${BASE}/drafts`, { recipe: draft }, authHeaders());
            setSaved(response.data.recipe);
            setDraft(response.data.recipe);
            if (onSaved) onSaved(response.data.recipe);
        } catch (e) { fail(e, t('recipeStudio.saveError', 'Le brouillon n’a pas pu être enregistré.')); }
        finally { setBusy(false); }
    };

    const testRun = async () => {
        if (!saved) return;
        setBusy(true); setError(null); setTestResult(null);
        try {
            const variables = {};
            Object.entries(draft.sample_values || {}).forEach(([k, v]) => { if (v && k !== 'password') variables[k] = v; });
            const response = await axios.post(`${BASE}/${encodeURIComponent(saved.db_id || saved.id)}/test-run`, { variables }, authHeaders());
            setTestResult(response.data);
            if (response.data.recipe) { setSaved(response.data.recipe); setDraft((d) => ({ ...d, last_test: response.data.recipe.last_test, status: response.data.recipe.status })); }
        } catch (e) { fail(e, t('recipeStudio.testError', 'La run de test a échoué.')); }
        finally { setBusy(false); }
    };

    const setStatus = async (status, catalog_proposal) => {
        if (!saved) return;
        setBusy(true); setError(null);
        try {
            const response = await axios.post(`${BASE}/${encodeURIComponent(saved.db_id || saved.id)}/status`, { status, catalog_proposal }, authHeaders());
            setSaved(response.data.recipe); setDraft(response.data.recipe);
            if (onSaved) onSaved(response.data.recipe);
        } catch (e) { fail(e, t('recipeStudio.statusError', 'Le changement de statut a été refusé.')); }
        finally { setBusy(false); }
    };

    const elements = (frame?.elements || []).filter((e) => !filter || JSON.stringify(e).toLowerCase().includes(filter.toLowerCase()));
    const canPublish = saved && draft && (draft.validation_errors || []).length === 0 && ['stopped_before_submit', 'success'].includes(draft.last_test?.status);

    return (
        <div className="rst-overlay" role="dialog" aria-modal="true">
            <div className="rst-panel">
                <div className="rst-head">
                    <h2>{t('recipeStudio.title', 'Studio de recettes')} · {carrierName}</h2>
                    <div className="rst-tabs">
                        <button type="button" className={mode === 'record' ? 'active' : ''} onClick={() => setMode('record')}><FaEye /> {t('recipeStudio.tabRecord', 'Enregistrer')}</button>
                        <button type="button" className={mode === 'review' ? 'active' : ''} onClick={() => setMode('review')} disabled={!draft}><FaListUl /> {t('recipeStudio.tabReview', 'Relire et publier')}</button>
                    </div>
                    <button type="button" className="rst-close" onClick={onClose} title={t('recipeStudio.close', 'Fermer')}><FaTimes /></button>
                </div>
                {error && <div className="rst-error">{error}</div>}

                {mode === 'record' && !session && (
                    <div className="rst-start">
                        <p>{t('recipeStudio.startIntro', 'Le navigateur tourne sur le serveur (IP de production). Réalisez le parcours une fois : choisissez un élément, une action, et marquez les valeurs qui varient d’un ticket à l’autre. Rien n’est envoyé au transporteur : l’étape d’envoi est seulement enregistrée.')}</p>
                        <div className="rst-row">
                            <label>{t('recipeStudio.operation', 'Opération')}
                                <select value={operation} onChange={(e) => setOperation(e.target.value)}>
                                    <option value="create">{t('carrierRecipes.op.create', 'création')}</option>
                                    <option value="update">{t('carrierRecipes.op.update', 'relance')}</option>
                                    <option value="monitor">{t('carrierRecipes.op.monitor', 'surveillance')}</option>
                                    <option value="pod">{t('carrierRecipes.op.pod', 'preuve')}</option>
                                </select>
                            </label>
                            <label>{t('recipeStudio.startUrl', 'URL du portail (optionnel si connue)')}
                                <input value={startUrl} onChange={(e) => setStartUrl(e.target.value)} placeholder="https://…" />
                            </label>
                            <button type="button" className="u-btn u-btn--primary" onClick={openSession} disabled={busy}><FaGlobe /> {busy ? t('recipeStudio.opening', 'Ouverture…') : t('recipeStudio.open', 'Ouvrir le portail')}</button>
                        </div>
                    </div>
                )}

                {mode === 'record' && session && (
                    <div className="rst-record">
                        <div className="rst-frame">
                            <div className="rst-url">{frame?.url}</div>
                            <img key={frameKey} src={`${BASE}/studio/sessions/${session.id}/screenshot?t=${frameKey}`} alt={t('recipeStudio.frameAlt', 'Page du portail')} onError={(e) => { e.target.style.display = 'none'; }} />
                            <div className="rst-steps">
                                <strong>{t('recipeStudio.recorded', 'Étapes enregistrées')} : {frame?.steps ?? 0}</strong>
                                <button type="button" className="u-btn u-btn--secondary" onClick={undo} disabled={busy}><FaUndo /> {t('recipeStudio.undo', 'Annuler la dernière')}</button>
                                <button type="button" className="u-btn u-btn--secondary" onClick={() => makeDraft(false)} disabled={busy}><FaListUl /> {t('recipeStudio.finish', 'Terminer → brouillon')}</button>
                                <button type="button" className="u-btn u-btn--secondary" onClick={() => makeDraft(true)} disabled={busy}><FaMagic /> {t('recipeStudio.finishReview', 'Terminer avec relecture IA')}</button>
                            </div>
                        </div>
                        <div className="rst-side">
                            <input className="rst-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t('recipeStudio.filter', 'Filtrer les éléments…')} />
                            <div className="rst-elements">
                                {elements.map((e) => (
                                    <button type="button" key={e.k} className={`rst-el ${selected?.k === e.k ? 'selected' : ''}`} onClick={() => setSelected(e)} title={e.css || ''}>
                                        <span className="rst-el-role">{e.role || e.tag}</span>
                                        <span className="rst-el-name">{e.label || e.text || e.placeholder || e.id || e.name || e.css}</span>
                                        {e.options && <span className="rst-el-opts">{e.options.length} {t('recipeStudio.options', 'options')}</span>}
                                    </button>
                                ))}
                                {elements.length === 0 && <div className="rst-muted">{t('recipeStudio.noElements', 'Aucun élément interactif visible.')}</div>}
                            </div>
                            <div className="rst-actions">
                                {selected && <div className="rst-selected">{t('recipeStudio.selected', 'Élément')} : <strong>{selected.label || selected.text || selected.placeholder || selected.id || selected.css}</strong></div>}
                                <div className="rst-row">
                                    <input value={value} onChange={(e) => setValue(e.target.value)} placeholder={t('recipeStudio.valuePlaceholder', 'Valeur à saisir / option à choisir')} type={secret ? 'password' : 'text'} />
                                    <select value={variable} onChange={(e) => setVariable(e.target.value)}>
                                        <option value="">{t('recipeStudio.literal', 'Valeur fixe')}</option>
                                        {VARIABLE_CHOICES.map((v) => <option key={v} value={v}>{'{'}{v}{'}'}</option>)}
                                    </select>
                                    <label className="rst-check"><input type="checkbox" checked={secret} onChange={(e) => setSecret(e.target.checked)} /> {t('recipeStudio.secret', 'secret')}</label>
                                </div>
                                <div className="rst-row rst-buttons">
                                    <button type="button" onClick={() => act('click')} disabled={busy || !selected}><FaMousePointer /> {t('recipeStudio.click', 'Cliquer')}</button>
                                    <button type="button" onClick={() => act('fill')} disabled={busy || !selected}><FaKeyboard /> {t('recipeStudio.fill', 'Saisir')}</button>
                                    <button type="button" onClick={() => act('select')} disabled={busy || !selected}><FaListUl /> {t('recipeStudio.select', 'Choisir')}</button>
                                    <button type="button" onClick={() => act('upload')} disabled={busy || !selected}><FaUpload /> {t('recipeStudio.upload', 'Pièces jointes')}</button>
                                    <button type="button" onClick={() => act('submit')} disabled={busy || !selected}><FaPaperPlane /> {t('recipeStudio.submit', 'Marquer comme envoi')}</button>
                                </div>
                                <div className="rst-row">
                                    <input value={waitText} onChange={(e) => setWaitText(e.target.value)} placeholder={t('recipeStudio.waitPlaceholder', 'Texte attendu sur la page')} />
                                    <button type="button" onClick={() => act('wait_text')} disabled={busy || !waitText}>{t('recipeStudio.wait', 'Attendre')}</button>
                                    <button type="button" onClick={() => act('assert_text')} disabled={busy || !waitText}>{t('recipeStudio.assert', 'Vérifier')}</button>
                                </div>
                                <div className="rst-row">
                                    <input value={startUrl} onChange={(e) => setStartUrl(e.target.value)} placeholder="https://…" />
                                    <button type="button" onClick={() => act('navigate', { url: startUrl })} disabled={busy || !startUrl}><FaGlobe /> {t('recipeStudio.navigate', 'Aller à')}</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {mode === 'review' && draft && (
                    <div className="rst-review">
                        <div className="rst-review-head">
                            <div>
                                <strong>{draft.id}</strong> · v{draft.version} · <span className={`rst-pill rst-pill-${draft.status}`}>{t(`carrierRecipes.state.${draft.status}`, draft.status)}</span>
                                {draft.last_test && <span className="rst-muted"> · {t('recipeStudio.lastTest', 'dernier test')} : {t(`carrierRecipes.runStatus.${draft.last_test.status}`, draft.last_test.status)}</span>}
                            </div>
                            <div className="rst-review-actions">
                                <button type="button" className="u-btn u-btn--secondary" onClick={save} disabled={busy || draft.status === 'published'}><FaSave /> {t('recipeStudio.save', 'Enregistrer le brouillon')}</button>
                                <button type="button" className="u-btn u-btn--secondary" onClick={testRun} disabled={busy || !saved}><FaPlay /> {t('recipeStudio.test', 'Run de test')}</button>
                                <button type="button" className="u-btn u-btn--primary" onClick={() => setStatus('published')} disabled={busy || !canPublish}><FaCheck /> {t('recipeStudio.publish', 'Publier pour ce compte')}</button>
                                {draft.status === 'published' && <button type="button" className="u-btn u-btn--secondary" onClick={() => setStatus('published', true)} disabled={busy}>{t('recipeStudio.propose', 'Proposer au catalogue')}</button>}
                                {draft.status === 'published' && <button type="button" className="u-btn u-btn--secondary" onClick={() => setStatus('retired')} disabled={busy}>{t('recipeStudio.retire', 'Retirer')}</button>}
                            </div>
                        </div>
                        {(draft.validation_errors || []).length > 0 && <div className="rst-error">{draft.validation_errors.join(' · ')}</div>}
                        {(draft.review_notes || []).length > 0 && <div className="rst-notes">{draft.review_notes.map((n, i) => <div key={i}>• {n}</div>)}</div>}
                        <div className="rst-meta">
                            <label>{t('recipeStudio.description', 'Description')}<input value={draft.description || ''} onChange={(e) => setDraft({ ...draft, description: e.target.value })} disabled={draft.status === 'published'} /></label>
                            <div className="rst-muted">{t('recipeStudio.variables', 'Variables')} : {(draft.variables || []).map((v) => `{${v}}`).join(', ') || '—'}</div>
                        </div>
                        <div className="rst-step-list">
                            {draft.steps.map((s, i) => (
                                <div key={s.id + i} className={`rst-step-row rst-step-${s.type}`}>
                                    <span className="rst-step-n">{i + 1}</span>
                                    <input className="rst-step-id" value={s.id} onChange={(e) => updateStep(i, { id: e.target.value })} disabled={draft.status === 'published'} />
                                    <span className="rst-step-type">{s.type}</span>
                                    {['fill', 'select'].includes(s.type) && <input className="rst-step-value" value={s.value ?? ''} onChange={(e) => updateStep(i, { value: e.target.value })} disabled={draft.status === 'published'} />}
                                    {s.type === 'open' && <span className="rst-step-target">{s.url}</span>}
                                    {['wait', 'assert'].includes(s.type) && <span className="rst-step-target">{s.text || (s.any || []).map((c) => c.text).filter(Boolean).join(' | ')}</span>}
                                    {s.target && <span className="rst-step-target" title={JSON.stringify(s.target)}>{(s.target.any || [s.target]).map((tt) => Object.entries(tt).map(([k, v]) => `${k}=${v}`).join(' ')).join(' | ')}</span>}
                                    {s.type !== 'open' && <label className="rst-check"><input type="checkbox" checked={!!s.optional} onChange={(e) => updateStep(i, { optional: e.target.checked })} disabled={draft.status === 'published'} /> {t('recipeStudio.optional', 'optionnel')}</label>}
                                    {draft.status !== 'published' && (
                                        <span className="rst-step-tools">
                                            <button type="button" onClick={() => moveStep(i, -1)}>↑</button>
                                            <button type="button" onClick={() => moveStep(i, 1)}>↓</button>
                                            <button type="button" onClick={() => removeStep(i)}>✕</button>
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                        {draft.enums && Object.keys(draft.enums).length > 0 && (
                            <div className="rst-enums">
                                <strong>{t('recipeStudio.enums', 'Listes relevées sur le portail')}</strong>
                                {Object.entries(draft.enums).map(([field, options]) => <div key={field}><em>{field}</em> : {options.join(' · ')}</div>)}
                            </div>
                        )}
                        {testResult && testResult.run && (
                            <div className={`rst-test rst-test-${testResult.run.status}`}>
                                <strong>{t(`carrierRecipes.runStatus.${testResult.run.status}`, testResult.run.status)}</strong> · {testResult.run.reached_step}
                                {testResult.run.error && <div className="rst-error">{testResult.run.error}</div>}
                                <div className="rst-test-steps">{(testResult.run.steps || []).map((s) => <span key={s.id} className={`rst-ts rst-ts-${s.status}`} title={[s.strategy, s.selector, s.error].filter(Boolean).join(' · ')}>{s.id}</span>)}</div>
                                {(testResult.captures || []).length > 0 && <div className="rst-captures">{testResult.captures.map((c, i) => c.url ? <a key={i} href={c.url} target="_blank" rel="noopener noreferrer">{c.step || `capture ${i + 1}`}</a> : null)}</div>}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default RecipeStudio;
